import { Add, Edit } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { AddVINDialog, GreenText, OrderableTableCell } from 'components'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { getToDoList as getToDoListAPI } from 'controllers/toDoLists/conformity/VehiclesEligibleForConformityController'
import { VehicleDto } from 'dtos'
import { VehicleEligibleForConformityDto, VehicleEligibleForConformityQueryParams } from 'dtos/toDoLists/conformity'
import { NeedsToBeSentOutgoingQueryParams } from 'dtos/toDoLists/michiganTitles'
import { useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'

export default function VehiclesEligibleForConformityToDoList() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const importBranchGuid = query.get('importBranchGuid') ?? undefined

  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [isAddingVIN, setIsAddingVIN] = useState<boolean>(false)
  const [isGettingToDoList, setIsGettingToDoList] = useState<boolean>(false)
  const [queryParams, setQueryParams] = useState<VehicleEligibleForConformityQueryParams>({ page: 0, pageSize: 10, order: 'asc', orderBy: 'createdDateTime' })
  const [toDoList, setToDoList] = useState<VehicleEligibleForConformityDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)

  const getToDoList = useDebounce((queryParams: NeedsToBeSentOutgoingQueryParams) => {
    setIsGettingToDoList(true)
    getToDoListAPI(queryParams)
      .then(res => {
        setTotalCount(res.totalCount ?? 0)
        setToDoList(res.value)
      })
      .finally(() => {
        setIsGettingToDoList(false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    if (getToDoList) getToDoList(queryParams)
  }, [getToDoList, queryParams])

  useEffect(() => {
    setQueryParams({ ...queryParams, importBranchGuid })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importBranchGuid])

  useEffect(() => {
    layout.setIsLayoutLoading(isGettingToDoList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGettingToDoList])

  useEffect(() => {
    layout.setTitle('Vehicles Eligible For Conformity')
    return () => layout.setTitle('')
  }, [layout])

  const onChangeOrder = (orderBy: string) => {
    setQueryParams(prev => ({
      ...queryParams,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  return (
    <>
      <AddVINDialog
        initialValues={new VehicleDto()}
        isLoading={isAddingVIN}
        onClose={() => setDialogOpen(false)}
        onSave={values => {
          setIsAddingVIN(true)
          controllers
            .createVehicle(values)
            .then(_ => {
              enqueueSnackbar('Vehicle Saved Successfully!', { variant: 'success' })
              setDialogOpen(false)
              getToDoList(queryParams)
            })
            .finally(() => {
              setIsAddingVIN(false)
            })
        }}
        open={dialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='createdDateTime'>Created</MenuItem>

                    <MenuItem value='vehicleCheckInDateTime'>Vehicle Check-In Completed Date</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>
                  </TextField>
                </Grid>

                {['', 'importerStockNumber', 'vin', 'year', 'make', 'model'].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => setFilterValue(e.target.value)}
                      type={filterField === 'year' ? 'number' : 'text'}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {['createdDateTime', 'vehicleCheckInDateTime'].includes(filterField) && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => setFilterValueFrom(e.target.value)}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => setFilterValueTo(e.target.value)}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField || (['createdDateTime', 'vehicleCheckInDateTime'].includes(filterField) ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      if (filterField === 'createdDateTime') {
                        setQueryParams({
                          ...queryParams,
                          createdDateTimeFrom: filterValueFrom,
                          createdDateTimeTo: filterValueTo
                        })
                      } else if (filterField === 'vehicleCheckInDateTime') {
                        setQueryParams({
                          ...queryParams,
                          vehicleCheckInDateTimeFrom: filterValueFrom,
                          vehicleCheckInDateTimeTo: filterValueTo
                        })
                      } else {
                        setQueryParams({
                          ...queryParams,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        queryParams.createdDateTimeFrom ||
                          queryParams.createdDateTimeTo ||
                          queryParams.vehicleCheckInDateTimeFrom ||
                          queryParams.vehicleCheckInDateTimeTo ||
                          queryParams.importerStockNumber ||
                          queryParams.vin ||
                          queryParams.year ||
                          queryParams.make ||
                          queryParams.model
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setQueryParams({
                        ...queryParams,
                        createdDateTimeFrom: undefined,
                        createdDateTimeTo: undefined,
                        vehicleCheckInDateTimeFrom: undefined,
                        vehicleCheckInDateTimeTo: undefined,
                        importerStockNumber: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item>
                <Button
                  color='primary'
                  endIcon={<Add />}
                  onClick={() => {
                    setDialogOpen(true)
                  }}
                  variant='text'
                >
                  ADD VIN
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {queryParams.createdDateTimeFrom && queryParams.createdDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created from "${moment(queryParams.createdDateTimeFrom).format('M/D/yyyy')}" to "${moment(queryParams.createdDateTimeTo).format(
                      'M/D/yyyy'
                    )}"`}
                    onDelete={() => setQueryParams({ ...queryParams, createdDateTimeFrom: undefined, createdDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vehicleCheckInDateTimeFrom && queryParams.vehicleCheckInDateTimeTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Check-In Completed Date from "${moment(queryParams.vehicleCheckInDateTimeFrom).format('M/D/yyyy')}" to "${moment(
                      queryParams.vehicleCheckInDateTimeTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vehicleCheckInDateTimeFrom: undefined, vehicleCheckInDateTimeTo: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock # contains "${queryParams.importerStockNumber}"`}
                    onDelete={() => setQueryParams({ ...queryParams, importerStockNumber: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains "${queryParams.vin}"`}
                    onDelete={() => setQueryParams({ ...queryParams, vin: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${queryParams.year}`}
                    onDelete={() => setQueryParams({ ...queryParams, year: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains "${queryParams.make}"`}
                    onDelete={() => setQueryParams({ ...queryParams, make: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}

              {queryParams.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains "${queryParams.model}"`}
                    onDelete={() => setQueryParams({ ...queryParams, model: undefined })}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <OrderableTableCell<VehicleEligibleForConformityDto>
                              keyName='createdDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof VehicleEligibleForConformityDto | undefined}
                            >
                              Created
                            </OrderableTableCell>

                            <OrderableTableCell<VehicleEligibleForConformityDto>
                              keyName='vehicleCheckInDateTime'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof VehicleEligibleForConformityDto | undefined}
                            >
                              Vehicle Check-In Completed Date
                            </OrderableTableCell>

                            <OrderableTableCell<VehicleEligibleForConformityDto>
                              keyName='importerStockNumber'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof VehicleEligibleForConformityDto | undefined}
                            >
                              Importer Stock #
                            </OrderableTableCell>

                            <OrderableTableCell<VehicleEligibleForConformityDto>
                              keyName='vin'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof VehicleEligibleForConformityDto | undefined}
                            >
                              VIN
                            </OrderableTableCell>

                            <OrderableTableCell<VehicleEligibleForConformityDto>
                              keyName='year'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof VehicleEligibleForConformityDto | undefined}
                            >
                              Year
                            </OrderableTableCell>

                            <OrderableTableCell<VehicleEligibleForConformityDto>
                              keyName='make'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof VehicleEligibleForConformityDto | undefined}
                            >
                              Make
                            </OrderableTableCell>

                            <OrderableTableCell<VehicleEligibleForConformityDto>
                              keyName='model'
                              onChangeOrder={onChangeOrder}
                              order={queryParams.order}
                              orderBy={queryParams.orderBy as keyof VehicleEligibleForConformityDto | undefined}
                            >
                              Model
                            </OrderableTableCell>

                            <TableCell padding='checkbox' size='small'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {toDoList.map((item, index) => (
                            <TableRow key={item.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.createdDateTime && moment().diff(moment(formatDate(item.createdDateTime)), 'days') >= 4
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight: item.createdDateTime && moment().diff(moment(formatDate(item.createdDateTime)), 'days') >= 4 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.createdDateTime ? formatDate(item.createdDateTime) : '-'}
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    item.vehicleCheckInDateTime && moment().diff(moment(item.vehicleCheckInDateTime), 'days') >= 2
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    item.vehicleCheckInDateTime && moment().diff(moment(item.vehicleCheckInDateTime), 'days') >= 2 ? 'bold' : 'inherit'
                                })}
                              >
                                {item.vehicleCheckInDateTime ? formatDate(item.vehicleCheckInDateTime) : '-'}
                              </TableCell>

                              <TableCell>{item.importerStockNumber}</TableCell>

                              <TableCell>{item.vin}</TableCell>

                              <TableCell>{item.year}</TableCell>

                              <TableCell>{item.make}</TableCell>

                              <TableCell>{item.model}</TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => navigate(`/vehicles/${encodeURIComponent(item.vehicleGuid)}/conformity`)}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={queryParams.pageSize}
                              page={queryParams.page}
                              onPageChange={(_, page) => {
                                setQueryParams({
                                  ...queryParams,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setQueryParams({
                                  ...queryParams,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Vehicles Eligible For Conformity To Do List**

Vehicles appear in this to-do list after Vehicle Check-In is completed, but "Sent to DOT" and/or "Conformity Package Tracking #" are blank under Vehicle Details – Conformity (tab).

This list is sorted by Created Date (oldest to newest).

The intended workflow for this screen is for users to review listed vehicles, navigate to the Conformity feature, and complete the necessary steps to submit a Conformity ZIP file to the DOT.

Ideally, this to-do list should remain empty at all times.

**Select To Do List Button:**

Clicking this button will open the “Select To-Do List” dialog.

**Add VIN Button:**

Clicking this button will open the “Add VIN” dialog.

**Filter Field Drop-Down:**

Clicking the drop-down displays a list of columns available for filtering:

   - Created,
   - Vehicle Check-In Completed Date.
   - Importer Stock #,
   - VIN,
   - Year,
   - Make,
   - Model

 **Filter Value:**

 The "Filter Value" field dynamically adjusts based on the selected column in the "Filter Field Drop-Down":

    - Created: Displays Filter Date From and Filter Date To.
    - Vehicle Check-In Completed Date: Displays Filter Date From and Filter Date To.
    - Importer Stock # a free-type filter field.
    - VIN: Displays a free-type filter field.
    - Year: Displays a free-type filter field.
    - Make: Displays a free-type filter field.
    - Model: Displays a free-type filter field.

**Apply Filter Button:**

Clicking this button applies the selected filtering options and updates the displayed data.

**Reset Filters:**

Clicking this button clears all applied filtering options, returning the data view to its default state.

**Created Column:**

The Created Date will be bold and red if:

Current Date ≥ Created Date + 4 days

**Vehicle Check-In Date Completed Column:**

The Vehicle Check-In Date will be bold and red if:

Current Date ≥ Vehicle Check-In Date + 2 days

The Vehicle Check-In Date represents the date the first vehicle check-in image was associated with the vehicle.

**Edit Action:**

Clicking the "EDIT" text button navigates the user to Vehicle Details – Conformity (tab) for that vehicle.`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
